// src/lib/api.ts
//
// API client for direct cross-origin calls to api.fhirfly.io.
// Uses credentials: "include" to send httpOnly cookies cross-origin.
// Auto-refreshes on 401 via /web/auth/refresh.

// `??` (not `||`) so an explicitly-empty VITE_API_URL means "use relative paths"
// — that routes calls through the Vite dev proxy (same-origin → working cookies).
const API_BASE = import.meta.env.VITE_API_URL ?? "https://devapi.fhirfly.io";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface FetchOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip auto-refresh on 401 (used internally to prevent infinite loops) */
  _skipRefresh?: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Try to refresh the session using the refresh token cookie.
 * Returns true if successful (new cookies set by the API).
 */
async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/web/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function apiFetch<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { method = "GET", body, headers = {}, _skipRefresh = false } = options;

  // Prepend API_BASE if endpoint starts with /
  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;

  const fetchHeaders: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest",
    ...headers,
  };
  if (body !== undefined) {
    fetchHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: fetchHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Auto-refresh on 401
  if (response.status === 401 && !_skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return apiFetch<T>(endpoint, { ...options, _skipRefresh: true });
    }
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      data?.error || `Request failed with status ${response.status}`,
      response.status,
      data
    );
  }

  return data as T;
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, headers?: Record<string, string>) =>
    apiFetch<T>(endpoint, { method: "GET", headers }),

  post: <T>(endpoint: string, body: unknown, headers?: Record<string, string>) =>
    apiFetch<T>(endpoint, { method: "POST", body, headers }),

  put: <T>(endpoint: string, body: unknown, headers?: Record<string, string>) =>
    apiFetch<T>(endpoint, { method: "PUT", body, headers }),

  patch: <T>(
    endpoint: string,
    body: unknown,
    headers?: Record<string, string>
  ) => apiFetch<T>(endpoint, { method: "PATCH", body, headers }),

  delete: <T>(endpoint: string, headers?: Record<string, string>) =>
    apiFetch<T>(endpoint, { method: "DELETE", headers }),
};
